import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  PRICING_VIEW_CHANGE_EVENT,
  PRICING_VIEW_KEY,
  PRICING_VIEW_NET,
  PRICING_VIEW_REGULAR,
  readNetPricingFromUrl,
} from "./FilterContext";

interface NetPriceContextType {
  showNetPrice: boolean;
  setShowNetPrice: (checked: boolean) => void;
  toggleShowNetPrice: () => void;
}

const NetPriceContext = createContext<NetPriceContextType | undefined>(
  undefined
);

const updatePricingViewInUrl = (checked: boolean) => {
  if (typeof window === "undefined") return;

  const url = new URL(window.location.href);
  url.searchParams.set(
    PRICING_VIEW_KEY,
    checked ? PRICING_VIEW_NET : PRICING_VIEW_REGULAR
  );

  const newUrl = `${url.pathname}?${url.searchParams.toString()}`;
  window.history.replaceState({ ...window.history.state }, "", newUrl);
  window.dispatchEvent(new Event(PRICING_VIEW_CHANGE_EVENT));
};

interface NetPriceProviderProps {
  children: ReactNode;
}

export const NetPriceProvider: React.FC<NetPriceProviderProps> = ({
  children,
}) => {
  const [showNetPrice, setShowNetPriceState] = useState(readNetPricingFromUrl);

  const setShowNetPrice = useCallback(
    (checked: boolean) => {
      setShowNetPriceState(checked);
      updatePricingViewInUrl(checked);
    },
    []
  );

  const toggleShowNetPrice = useCallback(() => {
    setShowNetPriceState((prev) => {
      const nextChecked = !prev;
      updatePricingViewInUrl(nextChecked);
      return nextChecked;
    });
  }, []);

  useEffect(() => {
    const syncPricingViewFromUrl = () => {
      setShowNetPriceState(readNetPricingFromUrl());
    };

    window.addEventListener("popstate", syncPricingViewFromUrl);
    window.addEventListener(PRICING_VIEW_CHANGE_EVENT, syncPricingViewFromUrl);

    return () => {
      window.removeEventListener("popstate", syncPricingViewFromUrl);
      window.removeEventListener(
        PRICING_VIEW_CHANGE_EVENT,
        syncPricingViewFromUrl
      );
    };
  }, []);

  const value = useMemo(
    () => ({
      showNetPrice,
      setShowNetPrice,
      toggleShowNetPrice,
    }),
    [showNetPrice, setShowNetPrice, toggleShowNetPrice]
  );

  return (
    <NetPriceContext.Provider value={value}>{children}</NetPriceContext.Provider>
  );
};

export const useNetPriceContext = (): NetPriceContextType => {
  const context = useContext(NetPriceContext);
  if (!context) {
    return {
      showNetPrice: readNetPricingFromUrl(),
      setShowNetPrice: (checked: boolean) => updatePricingViewInUrl(checked),
      toggleShowNetPrice: () =>
        updatePricingViewInUrl(!readNetPricingFromUrl()),
    };
  }
  return context;
};

export const resolveNetPriceField = <T,>(
  regular: T,
  net: T | undefined,
  showNetPrice: boolean
): T => {
  if (showNetPrice && net !== undefined && net !== null) {
    return net;
  }
  return regular;
};

export default NetPriceContext;
